import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import Animated, { useAnimatedScrollHandler } from 'react-native-reanimated';
import { useFocusEffect } from '@react-navigation/native';
import { AppView, Button, CardView, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { TimeToCompleteItem } from 'design-system/TimeToCompleteCard';
import { selectPotProductWithTheHighestInterest } from 'features/pots/reducers/selectors';
import { Tier } from 'features/premium/entities';
import { selectRentReportingAgenciesTierMap } from 'features/premium/selectors';
import { formatRentReportingAgencies } from 'features/rent-reporting/utils';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppSelector } from 'store/hooks';
import useColors from 'hooks/useColors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { selectIsGBUser } from 'reducers/selectors';
import { CompleteSetupStepId, } from '../completeSetup/entities';
import { useCompleteSetupSteps } from '../completeSetup/useCompleteSetupSteps';
const US_CA_STEP_IDS = [
    CompleteSetupStepId.ACTIVATE_TRIAL,
    CompleteSetupStepId.SECOND_BANK_ACCOUNT,
    CompleteSetupStepId.SET_UP_BUDGETING,
    CompleteSetupStepId.FRAUD_DETECTION,
];
const STEP_IDS = [
    CompleteSetupStepId.CREDIT_SCORE,
    CompleteSetupStepId.RENT_REPORTING,
    CompleteSetupStepId.START_SAVING,
    CompleteSetupStepId.SET_UP_BUDGETING,
];
export const hasWalkthroughEndStepToShow = (steps, isGBUser) => {
    const stepIds = isGBUser ? STEP_IDS : US_CA_STEP_IDS;
    return !!steps.find((step) => !step.isCompleted && stepIds.includes(step.id));
};
const WalkthroughEndedScreen = withAnimatedNavigationHeader(({ navigation, scrollY, route }) => {
    const onPressDecideLater = useCallback(() => {
        navigation.pop();
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerBackVisible: false,
            headerLeft: () => (<NavigationCloseButton onPress={onPressDecideLater}/>),
        });
    }, [onPressDecideLater]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT + rem(16), paddingHorizontalStyle);
    const onScroll = useAnimatedScrollHandler((scroll) => {
        // eslint-disable-next-line no-param-reassign
        scrollY.value = scroll.contentOffset.y;
    }, []);
    const { stepsToComplete, onStepPress } = useCompleteSetupSteps();
    const rentReportingAgenciesForTier = useAppSelector(selectRentReportingAgenciesTierMap);
    const potProductWithTheHighestInterest = useAppSelector(selectPotProductWithTheHighestInterest);
    const isGBUser = useAppSelector(selectIsGBUser);
    const steps = useMemo(() => {
        let stepsToShow = [];
        if (isGBUser) {
            stepsToShow = [
                {
                    id: CompleteSetupStepId.CREDIT_SCORE,
                    estimatedTimeToComplete: '< 2 minutes',
                    title: 'Unlock your credit score',
                    description: 'Track your score and get personalised insights to help build your financial health',
                },
                {
                    id: CompleteSetupStepId.RENT_REPORTING,
                    estimatedTimeToComplete: '< 2 minutes',
                    title: 'Improve my credit history',
                    description: `Start reporting your rent payments to ${formatRentReportingAgencies(rentReportingAgenciesForTier[Tier.ultimate])} and make them count towards your credit`,
                },
                {
                    id: CompleteSetupStepId.START_SAVING,
                    estimatedTimeToComplete: '< 3 minutes',
                    title: 'Grow my savings',
                    description: `Open a Pot and earn up to ${potProductWithTheHighestInterest?.rates.tierRates.find((t) => t.tier === Tier.ultimate)?.rate}% AER paid ${potProductWithTheHighestInterest?.interestPaymentFrequency?.toLowerCase()}. FSCS apply`,
                },
                {
                    id: CompleteSetupStepId.SET_UP_BUDGETING,
                    estimatedTimeToComplete: '< 3 minutes',
                    title: 'Reduce my spending',
                    description: 'Set up a budget to keep track of your outgoings and receive alerts',
                },
            ];
        }
        else {
            stepsToShow = [
                {
                    id: CompleteSetupStepId.ACTIVATE_TRIAL,
                    estimatedTimeToComplete: '< 1 minute',
                    title: 'Activate premium trial',
                    description: 'Try our full experience',
                },
                {
                    id: CompleteSetupStepId.SECOND_BANK_ACCOUNT,
                    estimatedTimeToComplete: '< 2 minutes',
                    title: 'Add a second bank account',
                    description: 'Make Emma work smarter',
                },
                {
                    id: CompleteSetupStepId.SET_UP_BUDGETING,
                    estimatedTimeToComplete: '< 3 minutes',
                    title: 'Reduce my spending',
                    description: 'Set up a budget to keep track of your outgoings and receive alerts',
                },
                {
                    id: CompleteSetupStepId.FRAUD_DETECTION,
                    estimatedTimeToComplete: '< 2 minutes',
                    title: 'Protect your identity',
                    description: 'Monitor your personal details and receive alerts if they leak',
                },
            ];
        }
        return stepsToShow.flatMap((step) => {
            const matchedStep = stepsToComplete.find((s) => s.id === step.id);
            return matchedStep
                ? [{ ...step, analyticsTag: matchedStep.analyticsTag }]
                : [];
        });
    }, [
        isGBUser,
        potProductWithTheHighestInterest?.interestPaymentFrequency,
        potProductWithTheHighestInterest?.rates.tierRates,
        rentReportingAgenciesForTier,
        stepsToComplete,
    ]);
    useFocusEffect(useCallback(() => {
        if (steps.length === 0) {
            navigation.pop();
        }
    }, [steps.length]));
    const colors = useColors();
    return (<AppView type="secondary" withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <Animated.ScrollView style={styles.scroll} contentContainerStyle={[styles.contentContainer, paddingBottom]} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} scrollEventThrottle={16} onScroll={onScroll}>
            <Text Text-24 style={styles.title}>
              {route.params.completed ? 'Tutorial completed! ' : ''}What do you
              want to do next?
            </Text>
            <Text TextThin-16 Secondary style={styles.paragraph}>
              You can come back to the other options later
            </Text>
            {steps.map((step, index, arr) => (<CardView noMargin key={step.id} style={index !== arr.length - 1 && styles.card}>
                <TimeToCompleteItem estimatedTimeToComplete={step.estimatedTimeToComplete} title={step.title} description={step.description} onPress={() => {
                onStepPress(step);
            }}/>
              </CardView>))}
          </Animated.ScrollView>
          <Button transparent floating title="Decide later" testID="laterButton" textColor={colors.text.secondary} custom gradientColors={[colors.background.dark, colors.background.dark]} onPress={onPressDecideLater}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styles = StyleSheet.create({
    scroll: {
        flex: 1,
    },
    contentContainer: {
        paddingHorizontal: rem(16),
        paddingTop: rem(8),
    },
    title: {
        marginBottom: rem(4),
    },
    paragraph: {
        marginBottom: rem(24),
    },
    card: {
        marginBottom: rem(16),
    },
});
export default WalkthroughEndedScreen;
